import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RefreshCw, Search, Send, Eye, MoreVertical, Undo2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { listOperationsByType } from '@/services/remittanceApi';
import StateBadge from '@/components/workflow/StateBadge';
import OperationDetailDrawer from '@/components/workflow/OperationDetailDrawer';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TERMINAL = new Set(['DELIVERED', 'COMPLETED', 'CANCELLED', 'REJECTED', 'FAILED']);
const REJECTED = new Set(['REJECTED', 'FAILED', 'CANCELLED']);

const RemesaList = ({ filter, title, emptyMsg, testid }) => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);

  // Eye-button target depends on the current role's layout:
  //   admin       → /operations/:id     (mounted under the admin shell)
  //   gestor      → /remesas/operations/:id  (under GestorLayout)
  //   provider    → /provider/operations/:id (under ProviderLayout)
  // Using the wrong path triggers the layout's role-guard which
  // bounces the user back to /login (the reported bug).
  const detailPath = (id) => {
    const role = user?.role;
    if (role === 'admin') return `/operations/${id}`;
    if (role === 'payment_agent' || role === 'ticket_provider') return `/provider/operations/${id}`;
    return `/remesas/operations/${id}`;
  };

  const handleCancelRefund = async (op) => {
    const msg = `¿Cancelar esta remesa y reintegrar ${op.metadata?.funding_amount || op.amount} ${op.metadata?.funding_currency || op.currency_from} a la wallet? Esto solo se hace si el proveedor ya la rechazó.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API}/operations/${op.id}/cancel-and-refund`, {}, { headers: getAuthHeaders() });
      toast.success('Remesa cancelada · monto reintegrado a la wallet');
      load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo cancelar'));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await listOperationsByType(getAuthHeaders, { type: 'remittance', limit: 200 });
      setOps(r.operations || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let items = ops.filter(filter);
    if (q) {
      const t = q.toLowerCase();
      items = items.filter((o) => (
        (o.id || '').toLowerCase().includes(t) ||
        (o.metadata?.beneficiary_name || '').toLowerCase().includes(t) ||
        (o.state || '').toLowerCase().includes(t) ||
        (o.currency_to || '').toLowerCase().includes(t)
      ));
    }
    return items;
  }, [ops, filter, q]);

  return (
    <div className="space-y-6" data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-xs text-slate-500 mt-1">{filtered.length} operación(es) en esta vista</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid={`${testid}-refresh`}><RefreshCw className="w-4 h-4 mr-1" />Refrescar</Button>
          <Link to="/remesas/enviar">
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid={`${testid}-new`}><Send className="w-4 h-4 mr-1" />Nueva remesa</Button>
          </Link>
        </div>
      </div>

      <Card className="border-0 shadow-sm p-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por ID, beneficiario, estado…" className="pl-9" data-testid={`${testid}-search`} />
        </div>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center p-12"><div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">{emptyMsg}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b text-left text-xs text-slate-500 uppercase">
                <th className="p-3">Beneficiario</th>
                <th className="p-3">Monto</th>
                <th className="p-3">Recibe</th>
                <th className="p-3">Tasa</th>
                <th className="p-3">Estado</th>
                <th className="p-3">Fecha</th>
                <th className="p-3 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} onClick={() => setSel(o)} className="border-b hover:bg-amber-50 cursor-pointer" data-testid={`row-${o.id}`}>
                  <td className="p-3">{o.metadata?.beneficiary_name || '—'}</td>
                  <td className="p-3 font-mono">${o.amount?.toFixed(2)} {o.currency_from}</td>
                  <td className="p-3 font-mono">{o.metadata?.recibe_aproximado ? `${Number(o.metadata.recibe_aproximado).toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${o.currency_to}` : '—'}</td>
                  <td className="p-3 font-mono text-xs">{o.metadata?.rate_locked ?? '—'}</td>
                  <td className="p-3"><StateBadge state={o.state} /></td>
                  <td className="p-3 text-slate-400 text-xs">{o.created_at && new Date(o.created_at).toLocaleString('es-ES')}</td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(detailPath(o.id))}
                        data-testid={`ver-detalle-${o.id}`}
                        title="Ver detalle"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`row-menu-${o.id}`} title="Más opciones">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(detailPath(o.id))} data-testid={`menu-detail-${o.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-2" /> Ver detalle
                          </DropdownMenuItem>
                          {o.state === 'REJECTED' && !o.metadata?.refunded_at && (
                            <DropdownMenuItem
                              onClick={() => handleCancelRefund(o)}
                              className="text-rose-600 focus:text-rose-700"
                              data-testid={`menu-cancel-refund-${o.id}`}
                            >
                              <Undo2 className="w-3.5 h-3.5 mr-2" /> Cancelar y reintegrar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <OperationDetailDrawer open={!!sel} op={sel} onClose={() => setSel(null)} onUpdated={load} />
    </div>
  );
};

export const GestorPendientesPage = () => (
  <RemesaList
    filter={(o) => !TERMINAL.has(o.state)}
    title="Remesas Pendientes"
    emptyMsg="Sin remesas pendientes 🎉"
    testid="gestor-pendientes-page"
  />
);

export const GestorRechazadasPage = () => (
  <RemesaList
    filter={(o) => REJECTED.has(o.state)}
    title="Remesas Rechazadas"
    emptyMsg="Sin remesas rechazadas"
    testid="gestor-rechazadas-page"
  />
);

export const GestorHistorialPage = () => (
  <RemesaList
    filter={() => true}
    title="Historial de Remesas"
    emptyMsg="Sin operaciones aún"
    testid="gestor-historial-page"
  />
);
